import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage, formatPhoneInput } from '@/lib/utils';

const BLANK = { first_name: '', last_name: '', email: '', phone: '', agency: '', notes: '' };

export default function WalkInDialog({ open, eventId, onClose, onDone }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const reset = () => { setForm(BLANK); setErrors({}); };
  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const submit = async () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Invalid email';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() || null : v])
      );
      await calendarAPI.addWalkIn(eventId, payload);
      toast.success('Walk-in added');
      reset();
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add walk-in'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="walk-in-dialog">
        <DialogHeader>
          <DialogTitle>Add Walk-In</DialogTitle>
          <DialogDescription className="text-xs">
            Register an on-site attendee. Marks Registration = registered and Attendance = walk-in.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First name *" value={form.first_name} onChange={(v) => setField('first_name', v)} error={errors.first_name} testId="wi-first-name" />
          <TextField label="Last name *" value={form.last_name} onChange={(v) => setField('last_name', v)} error={errors.last_name} testId="wi-last-name" />
          <TextField label="Email" type="email" value={form.email} onChange={(v) => setField('email', v)} error={errors.email} testId="wi-email" />
          <TextField label="Phone" value={formatPhoneInput(form.phone)} onChange={(v) => setField('phone', v)} testId="wi-phone" />
          <div className="col-span-2">
            <TextField label="Agency" value={form.agency} onChange={(v) => setField('agency', v)} testId="wi-agency" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-600">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="wi-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose?.(); }} data-testid="wi-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="wi-submit">
            {saving ? 'Adding…' : 'Add walk-in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TextField = ({ label, value, onChange, error, testId, type = 'text' }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={error ? 'border-rose-400' : ''} />
    {error && <p className="text-[11px] text-rose-600 mt-1">{error}</p>}
  </div>
);
